import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import * as Sentry from '@sentry/react';
import type { SessionCategories } from '@daddytime/sessions';
import { SessionsResponseSchema } from '@daddytime/api-contracts';
import { useAuth } from '../hooks/useAuth';
import { withTestCategory } from '../data/sessions';
import { API_BASE_URL } from '../config/api';
import { withSpan } from '../lib/telemetry';

export interface SessionsContextValue {
  sessions: SessionCategories;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

export const SessionsContext = createContext<SessionsContextValue | undefined>(
  undefined
);

interface SessionsProviderProps {
  children: ReactNode;
}

/**
 * Owns the session catalog. Fetches GET /sessions on mount and on every
 * auth identity change so the response always reflects what the backend
 * deems visible to the current caller. The backend is the single source
 * of truth — there is no local filter fallback.
 */
export const SessionsProvider: React.FC<SessionsProviderProps> = ({
  children,
}) => {
  const { user, loading: authLoading, getValidAuthHeaders, refreshTokens } =
    useAuth();

  const [sessions, setSessions] = useState<SessionCategories>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const [fetchToken, setFetchToken] = useState(0);
  const refetch = useCallback(() => setFetchToken((n) => n + 1), []);

  // Re-fetch whenever the auth identity changes or refetch() is called.
  // useEffect's dep array drives this on its own — no manual de-dup needed.
  const currentIdentity = user?.email ?? null;

  useEffect(() => {
    if (authLoading) return;

    let cancelled = false;
    const ac = new AbortController();
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const merged = await withSpan('catalog.load', {}, async (span) => {
          const url = new URL('sessions', API_BASE_URL);

          const doFetch = async () =>
            fetch(url, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                // Proactively refreshes an expiring token before the request,
                // so we usually avoid the 401 path entirely.
                ...(await getValidAuthHeaders()),
              },
              signal: ac.signal,
            });

          let response = await doFetch();

          // Belt-and-braces: if the token was still rejected (e.g. revoked
          // server-side, or it expired between the proactive check and the
          // request landing), refresh once and replay. If the refresh fails,
          // AuthContext has already cleared auth + toasted; the replay then
          // runs as anonymous.
          if (response.status === 401) {
            span.setAttribute('catalog.retried_after_401', true);
            const refreshed = await refreshTokens();
            span.setAttribute('catalog.token_refreshed', refreshed);
            response = await doFetch();
          }

          if (!response.ok) {
            throw new Error(
              `GET /sessions returned ${response.status}: ${response.statusText}`
            );
          }

          const rawBody = await response.json();
          // Parse against the shared schema so a backend shape change
          // can't silently break the catalog — we crash here rather than
          // render half-typed data.
          const body = SessionsResponseSchema.parse(rawBody);
          const withTests = withTestCategory(body.sessions as SessionCategories);
          const visibleSessions = Object.values(withTests).reduce(
            (sum, list) => sum + list.length,
            0
          );
          span.setAttribute('sessions.returned', visibleSessions);
          span.setAttribute('catalog.categories', Object.keys(withTests).length);
          return withTests;
        });
        if (cancelled) return;
        setSessions(merged);
      } catch (err) {
        if ((err as Error).name === 'AbortError' || cancelled) return;
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        Sentry.captureException(e, {
          level: 'warning',
          tags: { error_type: 'sessions_fetch_failed' },
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      ac.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, currentIdentity, fetchToken]);

  return (
    <SessionsContext.Provider value={{ sessions, loading, error, refetch }}>
      {children}
    </SessionsContext.Provider>
  );
};
